import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Navigation } from 'lucide-react';
import { companyInfo, phoneDigits, whatsappLink } from '../data/mock';
import { WhatsAppIcon } from './WhatsAppIcon';
import { toast } from '../hooks/use-toast';

const InfoRow = ({ icon: Icon, title, children, testId }) => (
  <div className="flex items-start gap-4" data-testid={testId}>
    <div className="w-11 h-11 bg-[#FF6B00]/10 rounded-lg flex items-center justify-center flex-shrink-0">
      <Icon className="text-[#FF6B00]" size={22} />
    </div>
    <div>
      <h3 className="text-white font-semibold mb-1">{title}</h3>
      <div className="text-gray-400 text-sm leading-relaxed">{children}</div>
    </div>
  </div>
);

const inputClass = "w-full px-4 py-3.5 bg-[#0a0a0a] border border-[#2a2a2a] rounded-md text-white text-base focus:outline-none focus:border-[#FF6B00] transition-colors";

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', phone: '', vehicle: '', message: '' });
  const [sending, setSending] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const text = `Olá! Meu nome é ${form.name}.\nVeículo: ${form.vehicle}\nProblema: ${form.message}\nMeu WhatsApp: ${form.phone}`;
    const waWindow = window.open('', '_blank');
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
    } catch {
      /* o WhatsApp abre mesmo se o registro falhar */
    } finally {
      if (waWindow) waWindow.location.href = whatsappLink(text);
      toast({ title: "Abrindo o WhatsApp...", description: "Sua mensagem já está preenchida, é só enviar." });
      setForm({ name: '', phone: '', vehicle: '', message: '' });
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
      <div>
        <label htmlFor="name" className="block text-white font-medium mb-2">Nome</label>
        <input id="name" name="name" value={form.name} onChange={onChange} required className={inputClass} placeholder="Seu nome" data-testid="contact-name-input" />
      </div>
      <div>
        <label htmlFor="phone" className="block text-white font-medium mb-2">WhatsApp</label>
        <input id="phone" name="phone" type="tel" value={form.phone} onChange={onChange} required className={inputClass} placeholder="(21) 90000-0000" data-testid="contact-phone-input" />
      </div>
      <div>
        <label htmlFor="vehicle" className="block text-white font-medium mb-2">Veículo / modelo / ano</label>
        <input id="vehicle" name="vehicle" value={form.vehicle} onChange={onChange} required className={inputClass} placeholder="Ex.: Scania R450 2019" data-testid="contact-vehicle-input" />
      </div>
      <div>
        <label htmlFor="message" className="block text-white font-medium mb-2">Problema</label>
        <textarea id="message" name="message" value={form.message} onChange={onChange} required rows="4" className={`${inputClass} resize-none`} placeholder="Descreva o problema..." data-testid="contact-message-input"></textarea>
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="contact-submit-button"
        className="w-full bg-[#25D366] text-white px-8 py-4 rounded-md hover:bg-[#1fb857] transition-all duration-300 font-bold text-lg flex items-center justify-center gap-3 disabled:opacity-60"
      >
        <WhatsAppIcon className="w-6 h-6" />
        {sending ? 'Abrindo WhatsApp...' : 'Enviar pelo WhatsApp'}
      </button>
    </form>
  );
};

const ContactSection = () => (
  <section id="contact" className="py-24 bg-[#111111]" data-testid="contact-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-14">
        <span className="text-[#FF6B00] font-bold text-sm uppercase tracking-wider">Fale Conosco</span>
        <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
          Peça seu <span className="text-[#FF6B00]">orçamento</span>
        </h2>
        <a
          href={whatsappLink()}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-whatsapp-main"
          className="inline-flex items-center gap-3 bg-[#25D366] text-white px-10 py-5 rounded-md hover:bg-[#1fb857] transition-all duration-300 font-bold text-lg md:text-xl transform hover:scale-105 shadow-[0_0_30px_rgba(37,211,102,0.35)]"
        >
          <WhatsAppIcon className="w-7 h-7" />
          Chamar no WhatsApp
        </a>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="space-y-8">
          <div className="bg-[#1a1a1a] p-8 rounded-lg border border-[#2a2a2a] space-y-7">
            <InfoRow icon={Phone} title="Telefone" testId="contact-phone">
              <a href={`tel:+55${phoneDigits}`} className="text-lg text-white hover:text-[#FF6B00] transition-colors font-semibold">{companyInfo.phone}</a>
            </InfoRow>
            <InfoRow icon={MapPin} title="Endereço" testId="contact-address">
              <a href={companyInfo.mapsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-[#FF6B00] transition-colors underline-offset-4 hover:underline">
                {companyInfo.address}
              </a>
            </InfoRow>
            <InfoRow icon={Clock} title="Horário de funcionamento" testId="contact-hours">
              {companyInfo.hours}
            </InfoRow>
            <InfoRow icon={Mail} title="E-mail" testId="contact-email">
              <a href={`mailto:${companyInfo.email}`} className="hover:text-[#FF6B00] transition-colors">{companyInfo.email}</a>
            </InfoRow>
            <a
              href={companyInfo.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="directions-button"
              className="flex items-center justify-center gap-2 w-full border-2 border-[#FF6B00] text-[#FF6B00] px-6 py-3.5 rounded-md hover:bg-[#FF6B00] hover:text-white transition-all duration-300 font-semibold"
            >
              <Navigation size={18} /> Como chegar
            </a>
          </div>

          <div className="rounded-lg overflow-hidden border border-[#2a2a2a] bg-[#1a1a1a]">
            <iframe
              title="Mapa – O.C. Gomes Sistema de Injeção Diesel"
              src={companyInfo.mapsEmbedUrl}
              width="100%"
              height="280"
              style={{ border: 0, filter: 'grayscale(20%) contrast(1.05)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            ></iframe>
          </div>
        </div>

        <div className="bg-[#1a1a1a] p-8 rounded-lg border border-[#2a2a2a] h-fit">
          <h3 className="text-white font-bold text-xl mb-1">Prefere escrever?</h3>
          <p className="text-gray-400 text-sm mb-6">Preencha e a mensagem abre pronta no seu WhatsApp.</p>
          <ContactForm />
        </div>
      </div>
    </div>
  </section>
);

export default ContactSection;
